import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Navigate, useRouterState } from '@tanstack/react-router'
import { type ReactNode, useEffect, useState } from 'react'
import {
  isCustomerSession,
  sessionQueryOptions,
  useDeviceTimezoneSync,
  useSession,
} from '@/entities/session'
import {
  ApiError,
  autoRedirectToLogin,
  clearLoginAttempt,
  hasLoginAttempt,
  redirectToLogin,
  UNAUTHORIZED_EVENT,
} from '@/shared/api'
import { BrandLogo } from '@/shared/ui/brand'
import { Button } from '@/shared/ui/button'
import { Spinner } from '@/shared/ui/spinner'
import { AppShell, allowedPrefixes } from '@/widgets/app-shell'
import { CommandPalette } from '@/widgets/command-palette'

// Pathless layout аутентифицированной части: единственный auth-гейт приложения.
// Своей формы логина НЕТ (00-architecture § 3) — нет сессии (401) уводит на
// единый вход хаба (22-unified-login), общий для сотрудника и заказчика.
// «Срез» данных обеспечивает бэк.
export const Route = createFileRoute('/_app')({
  component: () => (
    <AuthGate>
      <AppSurface />
    </AuthGate>
  ),
})

// Поверхность зависит от контура сессии (00-architecture § 6): у заказчика это
// витрина календаря, а студийные разделы ему не адресованы — заход по прямой
// ссылке возвращает в календарь, а не показывает отказ. Бэк режет те же данные
// сам, гейт здесь — про навигацию, а не про безопасность.
function AppSurface() {
  const session = useSession()
  // Ниже гейта сессия уже разрешена — единственное место, где контур смотрящего
  // известен до первого экрана, а значит есть кому сообщить зону устройства.
  useDeviceTimezoneSync()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const customer = isCustomerSession(session)
  const allowed = allowedPrefixes(session)

  if (customer && !allowed.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return <Navigate to="/calendar" replace />
  }

  return (
    <>
      <AppShell />
      {/* Поиск в customer-режиме скрыт — не цель первой фазы. */}
      {!customer && <CommandPalette />}
    </>
  )
}

// Брендированный полноэкранный статус — переходные/терминальные состояния входа.
function AuthScreen({ message, children }: { message: string; children?: ReactNode }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-bg-base px-4 py-12 text-text-primary">
      <div className="flex flex-col items-center gap-3 text-center">
        <BrandLogo className="h-7" />
        <div>
          <h1 className="t-h3">STIT Календарь</h1>
          <p className="mt-1 text-sm text-text-tertiary">Календарь команды студии</p>
        </div>
      </div>
      <div className="flex flex-col items-center gap-4 text-center">
        <p className="text-sm text-text-secondary">{message}</p>
        {children}
      </div>
    </main>
  )
}

// Гейт аутентифицированной зоны: identity контура смотрящего. Нет сессии (401, уже
// пережившей попытку продлить оба контура) → внешний редирект на единый вход
// хаба (00-architecture § 3, 22-unified-login). Прочая ошибка (сеть/бэкенд) —
// экран «Повторить», без зацикливания редиректа.
function AuthGate({ children }: { children: ReactNode }) {
  const me = useQuery(sessionQueryOptions)
  // 401 может прийти и mid-session из любого API-вызова (событие из client.ts) —
  // сессия истекла, работать всё равно нечем: тот же путь на SSO.
  const [sessionLost, setSessionLost] = useState(false)
  useEffect(() => {
    const onUnauthorized = () => setSessionLost(true)
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [])
  const unauthorized = sessionLost || (me.error instanceof ApiError && me.error.status === 401)

  // Попытка уже была → мы вернулись с SSO без сессии: показываем экран входа, а
  // autoRedirectToLogin в эффекте на этот раз никуда не уведёт.
  const loginFailed = unauthorized && hasLoginAttempt()
  useEffect(() => {
    if (unauthorized) autoRedirectToLogin()
  }, [unauthorized])
  // Сессия жива — следующий её обрыв снова имеет право на автопереход.
  useEffect(() => {
    if (me.isSuccess) clearLoginAttempt()
  }, [me.isSuccess])

  if (unauthorized) {
    return loginFailed ? (
      <AuthScreen message="Вход не завершился — сессия так и не установилась. Попробуйте ещё раз.">
        <Button
          onClick={() => {
            clearLoginAttempt()
            redirectToLogin()
          }}
        >
          Войти
        </Button>
      </AuthScreen>
    ) : (
      <AuthScreen message="Перенаправляем на вход hub.stit.tech…">
        <Spinner label="Перенаправляем на вход" />
      </AuthScreen>
    )
  }

  if (me.isPending) {
    return (
      <AuthScreen message="Проверяем сессию…">
        <Spinner label="Проверяем сессию" />
      </AuthScreen>
    )
  }

  if (me.isError) {
    return (
      <AuthScreen message="Не удалось проверить сессию. Проверьте сеть и попробуйте ещё раз.">
        <Button onClick={() => window.location.reload()}>Повторить</Button>
      </AuthScreen>
    )
  }

  return children
}
